import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  phone_verified: boolean | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userRoles: string[];
  userProfile: UserProfile | null;
  isAdmin: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function fetchProfile(userId: string, currentSession?: Session | null): Promise<UserProfile | null> {
  const headers: Record<string, string> = {};
  if (currentSession?.access_token) {
    headers["Authorization"] = `Bearer ${currentSession.access_token}`;
  }

  const { data, error } = await supabase
    .from("profiles")
    .select("id, first_name, last_name, phone, phone_verified")
    .eq("id", userId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching profile:", error);
    return null;
  }
  return data;
}

async function fetchRoles(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);

  if (error) {
    console.error("Error fetching roles:", error);
    return [];
  }
  return data?.map((r) => r.role) ?? [];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const loadUserData = useCallback(async (currentUser: User, currentSession: Session | null) => {
    const [profile, roles] = await Promise.all([
      fetchProfile(currentUser.id, currentSession),
      fetchRoles(currentUser.id),
    ]);
    setUserProfile(profile);
    setUserRoles(roles);
  }, []);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          // Defer data fetching to avoid Supabase client deadlock
          setTimeout(() => loadUserData(currentSession.user, currentSession), 0);
        } else {
          setUserProfile(null);
          setUserRoles([]);
        }
        setLoading(false);
      }
    );

    // THEN check existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      if (existingSession?.user) {
        setSession(existingSession);
        setUser(existingSession.user);
        loadUserData(existingSession.user, existingSession);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [loadUserData]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setUserProfile(null);
    setUserRoles([]);
  }, []);

  const isAdmin = userRoles.includes("admin");

  return (
    <AuthContext.Provider value={{ user, session, loading, userRoles, userProfile, isAdmin, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
